import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';
import {
  Integration,
  IntegrationTypeComingsoon,
} from '../models/integration/integration';
import {
  CrawlerConfig,
  CrawlerDiscoveryStrategyType,
  CrawlerOutput,
  CrawlerProgress,
  CrawlerRecurrence,
  CrawlerStatus,
  CrawlerType,
  IntegrationWithCrawler,
  CrawlerLaunchEligibility,
} from '../models/integration/crawler';
import { Trademark } from '../models/trademark';
import { Paginator } from '../models/paginator';
import { Batch } from '../models/batch';
import { DiscoveredUrlsByDepthData } from '../models/discoveredUrls';

export interface Data {
  _id: CrawlerType;
  count: number;
}

export type CrawlerMode = 'single' | 'excel' | 'discovery';

interface BaseCrawlerCreationParams {
  name?: string;
  recurrence: CrawlerRecurrence;
  trademarks: string[];
  // Selected codes (parents kept as-is); only DISCOVERY expands them server-side.
  countryCodes?: string[];
  config?: Partial<CrawlerConfig>;
}

export interface SingleCrawlerCreationParams extends BaseCrawlerCreationParams {
  crawlerMode: 'single';
  url: string;
}

export interface ExcelCrawlerCreationParams extends BaseCrawlerCreationParams {
  crawlerMode: 'excel';
  excelFile: File;
  urlColumn: string;
}

export interface DiscoveryCrawlerCreationParams extends BaseCrawlerCreationParams {
  crawlerMode: 'discovery';
  url: string;
  strategy: CrawlerDiscoveryStrategyType;
  // Not applicable to the AMAZON strategy.
  depth?: number;
  maxPages: number;
}

export type CrawlerCreationParams =
  | SingleCrawlerCreationParams
  | ExcelCrawlerCreationParams
  | DiscoveryCrawlerCreationParams;

@Injectable({
  providedIn: 'root'
})
export class IntegrationService {

  constructor(
    private http: HttpClient
  ) { }

  icon(integrationType: string): string {
    switch (integrationType) {
      case 'WEBSITE':
        return 'language';
      case IntegrationTypeComingsoon.MAIL:
        return 'email';
      case IntegrationTypeComingsoon.INVOICING:
        return 'receipt_long';
      case IntegrationTypeComingsoon.DOCUMENT_MANAGEMENT:
        return 'folder';
      case IntegrationTypeComingsoon.SOCIAL_NETWORK:
        return 'share';
      default:
        return 'question_mark';
    }
  }

  // The crawler family is exposed as a single "WEBSITE" tile (its dialog
  // handles SINGLE_URL/EXCEL_BATCH/DISCOVERY sub-modes); other kinds are
  // placeholders.
  types() {
    return [
      {
        key: 'WEBSITE',
        icon: this.icon('WEBSITE'),
        logo: undefined,
        available: true,
        title: 'INTEGRATIONS.WEBSITE.TITLE',
        description: 'INTEGRATIONS.WEBSITE.DESCRIPTION',
      },
      ...Object.keys(IntegrationTypeComingsoon).map(key => ({
        key,
        icon: this.icon(key),
        logo: undefined,
        available: false,
        title: `INTEGRATIONS.${key}.TITLE`,
        description: `INTEGRATIONS.${key}.DESCRIPTION`,
      })),
    ];
  }

  retrieveAll(
    pageNumber: number,
    limit: number,
    offset: number,
    sort?: string,
    type?: CrawlerType[],
    recurrence?: CrawlerRecurrence[],
    trademark?: Trademark[],
    query?: string,
    status?: CrawlerStatus[],
    countries?: string[],
  ): Promise<Paginator<IntegrationWithCrawler>> {
    let params = new HttpParams()
      .set('page', pageNumber)
      .set('limit', limit)
      .set('offset', offset);

    if (sort) {
      params = params.set('sort', sort);
    }

    if (type) {
      type.forEach(t => {
        params = params.append('type[]', t);
      });
    }

    if (recurrence) {
      recurrence.forEach(r => {
        params = params.append('recurrence[]', r);
      });
    }

    if (status) {
      status.forEach(s => {
        params = params.append('status[]', s);
      });
    }

    if (countries) {
      countries.forEach(c => {
        params = params.append('countries[]', c);
      });
    }

    if (trademark) {
      trademark.forEach(tm => {
        if (tm._id) {
          params = params.append('trademark[]', tm._id);
        }
      });
    }

    if (query) {
      params = params.set('q', query);
    }

    return firstValueFrom(
      this.http.get<Paginator<IntegrationWithCrawler>>(
        `${environment.backend}/integrations/search`,
        { params }
      )
    );
  }

  aggregate(): Promise<Data[]> {
    return firstValueFrom(
      this.http.get<Data[]>(
        `${environment.backend}/integrations/aggregate/type`
      )
    );
  }

  // Backend currently only accepts { name } (rename). Pending a new endpoint
  // to update config/recurrence/trademarks — see CRAWLER_API_MIGRATION.md.
  rename(id: string, name: string): Promise<Integration> {
    return firstValueFrom(
      this.http.put<Integration>(
        `${environment.backend}/integrations/${id}`,
        { name }
      )
    );
  }

  trigger(
    id: string,
    retryFailed?: boolean,
    mode?: 'discover' | 'crawl',
  ): Promise<void> {
    let params = new HttpParams();

    if (retryFailed) {
      params = params.set('failed', true);
    }

    if (mode) {
      params = params.set('mode', mode);
    }

    return firstValueFrom(
      this.http.post<void>(
        `${environment.backend}/integrations/${id}/launch`,
        null,
        { params }
      )
    );
  }

  delete(id: string): Promise<void> {
    return firstValueFrom(
      this.http.delete<void>(`${environment.backend}/integrations/${id}`)
    );
  }

  cancelExecution(crawlerId: string, executionId: string): Promise<void> {
    return firstValueFrom(
      this.http.post<void>(
        `${environment.backend}/integrations/${crawlerId}/executions/${executionId}/cancel`,
        null
      )
    );
  }

  // Deletes every page the exploration found and launches it again.
  rediscover(crawlerId: string): Promise<void> {
    return firstValueFrom(
      this.http.post<void>(`${environment.backend}/integrations/backlinks/${crawlerId}/rediscover`, null),
    );
  }

  cancelDiscovery(crawlerId: string): Promise<void> {
    return firstValueFrom(
      this.http.post<void>(
        `${environment.backend}/integrations/${crawlerId}/discovery/cancel`,
        null
      )
    );
  }

  // Multipart: one `payload` field (JSON-stringified, discriminated on
  // crawlerMode) + an `excel` file when in excel mode.
  createCrawler(params: CrawlerCreationParams): Promise<IntegrationWithCrawler> {
    const payload: Record<string, unknown> = {
      crawlerMode: params.crawlerMode,
      name: params.name,
      trademarks: params.trademarks,
      recurrence: params.recurrence,
      config: params.config,
    };

    // Common to all types — selected codes; expanded server-side at discovery launch.
    if (params.countryCodes?.length) {
      payload['countryCodes'] = params.countryCodes;
    }

    switch (params.crawlerMode) {
      case 'single':
        payload['url'] = params.url;
        break;
      case 'excel':
        payload['urlColumn'] = params.urlColumn;
        break;
      case 'discovery':
        payload['url'] = params.url;
        payload['strategy'] = params.strategy;
        payload['maxPages'] = params.maxPages;
        if (params.depth !== undefined) {
          payload['depth'] = params.depth;
        }
        break;
    }

    const formData = new FormData();
    formData.append('payload', JSON.stringify(payload));
    if (params.crawlerMode === 'excel') {
      formData.append('excel', params.excelFile);
    }

    return firstValueFrom(
      this.http.post<IntegrationWithCrawler>(
        `${environment.backend}/integrations/website-crawler`,
        formData
      )
    );
  }

  retrieveIntegration(id: string): Promise<IntegrationWithCrawler> {
    return firstValueFrom(
      this.http.get<IntegrationWithCrawler>(
        `${environment.backend}/integrations/${id}`
      )
    );
  }

  //Launch eligibility + live status for the single-crawler wizard action buttons
  // Also carries the step gates (discoveryGate / crawlGate) for the stepper.
  retrieveLaunchEligibility(id: string): Promise<CrawlerLaunchEligibility> {
    return firstValueFrom(
      this.http.get<CrawlerLaunchEligibility>(
        `${environment.backend}/integrations/${id}/launch-eligibility`
      )
    );
  }

  // Batches produced by this integration (crawler), newest first, paginated
  retrieveBatches(
    id: string,
    page: number,
    limit: number,
    offset: number,
    sort?: string,
    query?: string,
  ): Promise<Paginator<Batch>> {
    let params = new HttpParams()
      .set('page', page)
      .set('limit', limit)
      .set('offset', offset);
    if (sort) {
      params = params.set('sort', sort);
    }
    if (query) {
      params = params.set('q', query);
    }
    return firstValueFrom(
      this.http.get<Paginator<Batch>>(
        `${environment.backend}/integrations/${id}/batches`,
        { params },
      )
    );
  }

  retrieveDiscoveredUrlsByDepth(id: string): Promise<DiscoveredUrlsByDepthData[]> {
    return firstValueFrom(
      this.http.get<DiscoveredUrlsByDepthData[]>(
        `${environment.backend}/integrations/${id}/discovery-graphs`
      )
    );
  }


  getCrawlerProgress(
    id: string,
  ): Promise<{ progress: CrawlerProgress; output?: CrawlerOutput }> {
    return firstValueFrom(
      this.http.get<{ progress: CrawlerProgress; output?: CrawlerOutput }>(
        `${environment.backend}/integrations/${id}/progress`
      )
    );
  }
}
